import { effectiveFitSizes, inferFitSizes } from './variantFitSizes';
import { VariantFitSizesField } from './VariantFitSizesField';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import styled from 'styled-components';
import {Button} from '../../design-system/components/Button';
import {Divider} from '../../design-system/components/Divider';
import {Input} from '../../design-system/components/Input';
import {StatusBadge} from '../../design-system/components/StatusBadge';
import {designTokens as t} from '../../design-system/tokens/designTokens';
import {AdminApiError} from '../api/errors';
import {
  createAdminVariant,
  getAdminProductVariants,
  updateAdminVariantSize,
  type AdminProductDetailVariant,
  type AdminVariant,
} from '../api/products';
import {
  isVariantEditorDirty,
  projectDetailVariants,
  reconcileCreatedVariant,
  reconcileUnknownCreate,
  reconcileUpdatedVariant,
  sortAdminVariants,
  validateVariantSize,
  variantSizeErrorCopy,
  type VariantEditorTarget,
} from './productVariantsModel';
import {ProductInventoryItems, type InventoryRetireIntent} from './ProductInventoryItems';
import {useProductInventoryController} from './useProductInventoryController';
import type {ActivationDomainGuardSnapshot} from './productActivationModel';
import type {InventoryEditorTarget,InventoryRiskMeta} from './productInventoryModel';

const Section = styled.section`
  inline-size: 100%;
  max-inline-size: 840px;
  min-inline-size: 0;
  margin-block-start: ${t.space[4]};
  display: grid;
  gap: ${t.space[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[4]};

  @media (max-width: 767px) {
    align-items: stretch;
    flex-direction: column;

    > button { inline-size: 100%; }
  }
`;

const Heading = styled.h2`
  &:focus{outline:2px solid ${t.color.focus.ring};outline-offset:2px;}
  margin: 0;
  font: 600 22px/30px ${t.font.family.ui};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${t.color.border.subtle};
  border-radius: ${t.radius[2]};
  overflow: visible;
`;

const Item = styled.li`
  padding: ${t.space[4]};
  min-inline-size: 0;

  & + & { border-block-start: 1px solid ${t.color.border.subtle}; }
`;

const Row = styled.div`
  display: grid;
  grid-template-areas:
    "size"
    "status"
    "sku"
    "edit";
  grid-template-columns: minmax(0, 1fr);
  gap: ${t.space[3]};
  align-items: center;
  min-inline-size: 0;

  @media (min-width: 768px) and (max-width: 1023px) {
    grid-template-areas:
      "size status"
      "sku edit";
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: ${t.space[6]};
  }

  @media (min-width: 1024px) {
    grid-template-areas: "size status sku edit";
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto;
    column-gap: ${t.space[6]};
  }
`;

const Size = styled.div`
  grid-area: size;
  min-inline-size: 0;
  display: grid;
  gap: ${t.space[1]};
  overflow-wrap: anywhere;

  > span:first-child {
    color: ${t.color.text.secondary};
    font-size: ${t.type.caption.size};
    line-height: ${t.type.caption.lineHeight};
  }

  > span[data-variant-size] { font-weight: ${t.font.weight.semibold}; }
`;
const VariantStatus = styled.div`grid-area: status; justify-self: start;`;
const Sku = styled.div`
  grid-area: sku;
  min-inline-size: 0;
  color: ${t.color.text.secondary};
  overflow-wrap: anywhere;
`;
const EditAction = styled.div`
  grid-area: edit;

  @media (max-width: 767px) {
    > button { inline-size: 100%; }
  }
`;

const Editor = styled.div`
  margin-block-start: ${t.space[4]};
  display: grid;
  gap: ${t.space[3]};
  min-inline-size: 0;
`;

const EditorActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${t.space[2]};

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: 1fr;

    > button { inline-size: 100%; }
  }
`;

const Empty = styled.div`
  padding: ${t.space[6]};
  display: grid;
  gap: ${t.space[3]};
  border: 1px solid ${t.color.border.subtle};
  border-radius: ${t.radius[2]};
  color: ${t.color.text.secondary};

  strong { color: ${t.color.text.primary}; }

  @media (max-width: 767px) {
    > button { inline-size: 100%; }
  }
`;

const Message = styled.p`
  margin: 0;
  color: ${t.color.text.secondary};
  overflow-wrap: anywhere;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${t.color.status.danger.strong};
  font-size: ${t.type.bodySm.size};
  line-height: ${t.type.bodySm.lineHeight};
`;

export type VariantyEditorTarget =
  | {domain:'variant';target:VariantEditorTarget}
  | {domain:'inventory';target:InventoryEditorTarget};

export interface EditorSwitchIntent {
  dirtyDomain:'variant'|'inventory';
  target:VariantyEditorTarget;
}

export interface ProductVariantsSectionHandle {
  resolveCurrentEditorFocus(): HTMLElement | null;
  focusCurrentEditor(): void;
  discardAndOpen(target: VariantyEditorTarget): void;
  retireInventoryItem(input:{variantId:string;inventoryItemId:string}): Promise<boolean>;
  getActivationGuardSnapshot():ActivationDomainGuardSnapshot;
}

export interface ProductVariantsSectionProps {
  productId: string;
  token: string;
  initialVariants: AdminProductDetailVariant[];
  onRiskChange?(risk: boolean): void;
  onPendingRiskChange?(pending: boolean): void;
  onInventoryRiskChange?(risk: InventoryRiskMeta): void;
  onRequestEditorSwitch?(intent: EditorSwitchIntent): void;
  onRequestInventoryRetire?(intent: InventoryRetireIntent): void;
  onAccessError?(error: unknown): boolean;
}

type Operation = null | 'create' | `update:${string}`;
type RefreshReason = null | 'unknown-create' | 'missing-variant' | 'sku-conflict' | 'refresh-failed';

function available(element: HTMLElement | null | undefined): element is HTMLElement {
  if (!element?.isConnected || element.matches(':disabled,[aria-disabled="true"]') || element.closest('[hidden],[inert],[aria-hidden="true"]')) return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

export const ProductVariantsSection = forwardRef<ProductVariantsSectionHandle, ProductVariantsSectionProps>(
  function ProductVariantsSection(props, ref) {
    const [canonicalVariants, setCanonicalVariants] = useState<AdminVariant[]>(() =>
      sortAdminVariants(projectDetailVariants(props.initialVariants)),
    );
    const [activeEditor, setActiveEditor] = useState<VariantEditorTarget | null>(null);
    const [sizeDraft, setSizeDraft] = useState('');
    const [fitSizesDraft, setFitSizesDraft] = useState<string[] | null>(null);
    const fitSizes = useMemo(() => fitSizesDraft ?? (sizeDraft.trim() ? inferFitSizes(sizeDraft) : []), [fitSizesDraft, sizeDraft]);
    const fitSizesRef = useRef(fitSizes); fitSizesRef.current = fitSizes;
    const sizeDraftRef=useRef(sizeDraft);sizeDraftRef.current=sizeDraft;
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [operation, setOperation] = useState<Operation>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshReason, setRefreshReason] = useState<RefreshReason>(null);
    const [unknownCreate, setUnknownCreate] = useState<string | null>(null);
    const [missingVariantId, setMissingVariantId] = useState<string | null>(null);
    const [productMissing, setProductMissing] = useState(false);
    const inventory = useProductInventoryController({
      productId: props.productId,
      token: props.token,
      initialVariants: props.initialVariants,
      onAccessError: props.onAccessError,
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const editorTriggerRef = useRef<HTMLElement | null>(null);
    const pendingSwitchTriggerRef = useRef<HTMLElement | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const mounted = useRef(true);
    const productRef = useRef(props.productId);
    const mutationGeneration = useRef(0);
    const refreshGeneration = useRef(0);
    const mutationController = useRef<AbortController | null>(null);
    const refreshController = useRef<AbortController | null>(null);
    const focusGeneration = useRef(0);

    const editorDirty = useMemo(() => isVariantEditorDirty({
      target: activeEditor,
      sizeDraft,
      fitSizesDraft: fitSizes,
      variants: canonicalVariants,
    }), [activeEditor, canonicalVariants, sizeDraft, fitSizes]);
    const activationPendingRef=useRef(false);activationPendingRef.current=Boolean(operation);
    const risk = editorDirty || operation !== null || unknownCreate !== null;

    const onRiskChange = props.onRiskChange;
    const onPendingRiskChange = props.onPendingRiskChange;
    const onInventoryRiskChange = props.onInventoryRiskChange;
    useEffect(() => onRiskChange?.(risk), [onRiskChange, risk]);
    useEffect(() => onPendingRiskChange?.(Boolean(operation || unknownCreate)), [onPendingRiskChange, operation, unknownCreate]);
    useEffect(() => onInventoryRiskChange?.(inventory.riskMeta), [onInventoryRiskChange, inventory.riskMeta]);

    useEffect(() => {
      mounted.current = true;
      productRef.current = props.productId;
      return () => {
        mounted.current = false;
        mutationGeneration.current += 1;
        refreshGeneration.current += 1;
        focusGeneration.current += 1;
        mutationController.current?.abort();
        refreshController.current?.abort();
      };
    }, [props.productId]);

    const scheduleFocus = useCallback((resolve: () => HTMLElement | null | undefined) => {
      const generation = ++focusGeneration.current;
      const productId = props.productId;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!mounted.current || productRef.current !== productId || focusGeneration.current !== generation) return;
        if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
        const target = resolve();
        if (available(target)) target.focus();
      }));
    }, [props.productId]);

    const fallbackFocus = useCallback(() => {
      const root = sectionRef.current;
      if (!root) return null;
      const control = Array.from(root.querySelectorAll<HTMLElement>('button,input')).find(available);
      return control ?? root.querySelector<HTMLElement>('#product-variants-title');
    }, []);

    const focusEditButton = (variantId: string) => scheduleFocus(() => {
      const root = sectionRef.current;
      const row = Array.from(root?.querySelectorAll<HTMLElement>('[data-variant-id]') ?? [])
        .find(item => item.dataset.variantId === variantId);
      return row?.querySelector<HTMLElement>('[data-variant-edit]') ?? fallbackFocus();
    });

    const openEditor = useCallback((target: VariantEditorTarget, trigger?: HTMLElement | null) => {
      if (operation || productMissing) return;
      editorTriggerRef.current = trigger ?? pendingSwitchTriggerRef.current;
      pendingSwitchTriggerRef.current = null;
      setActiveEditor(target);
      const variant = target.kind === 'edit' ? canonicalVariants.find(item => item.id === target.variantId) : undefined;
      setFitSizesDraft(variant ? effectiveFitSizes(variant) : null);
      setSizeDraft(target.kind === 'add'
        ? ''
        : canonicalVariants.find(variant => variant.id === target.variantId)?.size ?? '');
      setFieldError(null);
      setSubmitError(null);
      setMissingVariantId(null);
      setFeedback(null);
      scheduleFocus(() => inputRef.current ?? fallbackFocus());
    }, [canonicalVariants, fallbackFocus, operation, productMissing, scheduleFocus]);

    const clearVariantEditor = () => {
      setActiveEditor(null);
      setSizeDraft('');
      setFitSizesDraft(null);
      setFieldError(null);
      setSubmitError(null);
      setMissingVariantId(null);
      setFeedback(null);
      editorTriggerRef.current = null;
    };

    const inventorySection = useCallback((variantId: string) =>
      Array.from(sectionRef.current?.querySelectorAll<HTMLElement>('[data-inventory-variant]') ?? [])
        .find(section => section.dataset.inventoryVariant === variantId) ?? null, []);

    const resolveInventoryEditorFocus = useCallback((target?: InventoryEditorTarget | null) => {
      const active = target ?? inventory.activeEditor;
      if (!active) return null;
      const section = inventorySection(active.variantId);
      if (!section) return sectionRef.current?.querySelector<HTMLElement>('#product-variants-title') ?? null;
      const field = active.kind === 'add'
        ? section.querySelector<HTMLElement>('[data-inventory-code]')
        : section.querySelector<HTMLElement>('[data-inventory-condition]');
      return available(field) ? field : section.querySelector<HTMLElement>('h3') ?? null;
    }, [inventory.activeEditor, inventorySection]);

    const inventoryScheduleFocus = inventory.scheduleFocus;
    const focusInventoryEditor = useCallback((target: InventoryEditorTarget) =>
      inventoryScheduleFocus(() => resolveInventoryEditorFocus(target)),
    [inventoryScheduleFocus, resolveInventoryEditorFocus]);

    const requestEditor = (target: VariantEditorTarget, trigger: HTMLElement) => {
      if (operation || productMissing || unknownCreate || inventory.riskMeta.editorPendingOrUnresolved) return;
      const same = activeEditor?.kind === target.kind &&
        (target.kind === 'add' || (activeEditor?.kind === 'edit' && activeEditor.variantId === target.variantId));
      if (same) return;
      if (inventory.activeEditor) {
        if (inventory.riskMeta.hasDraft) {
          pendingSwitchTriggerRef.current = trigger;
          props.onRequestEditorSwitch?.({dirtyDomain:'inventory',target:{domain:'variant',target}});
          return;
        }
        inventory.discardCurrent();
      }
      if (editorDirty) {
        pendingSwitchTriggerRef.current = trigger;
        props.onRequestEditorSwitch?.({dirtyDomain:'variant',target:{domain:'variant',target}});
        return;
      }
      openEditor(target, trigger);
    };

    const requestInventoryEditor = (target: InventoryEditorTarget, trigger: HTMLElement) => {
      if (operation || productMissing || unknownCreate || inventory.riskMeta.editorPendingOrUnresolved) return;
      if (inventory.activeEditor) {
        const same = inventory.activeEditor.kind === target.kind &&
          inventory.activeEditor.variantId === target.variantId &&
          (target.kind === 'add' || (inventory.activeEditor.kind === 'edit' && inventory.activeEditor.inventoryItemId === target.inventoryItemId));
        if (same) return;
        if (inventory.riskMeta.hasDraft) {
          pendingSwitchTriggerRef.current = trigger;
          props.onRequestEditorSwitch?.({dirtyDomain:'inventory',target:{domain:'inventory',target}});
          return;
        }
        inventory.discardCurrent();
      }
      if (activeEditor) {
        if (editorDirty) {
          pendingSwitchTriggerRef.current = trigger;
          props.onRequestEditorSwitch?.({dirtyDomain:'variant',target:{domain:'inventory',target}});
          return;
        }
        clearVariantEditor();
      }
      if (inventory.open(target, trigger)) focusInventoryEditor(target);
    };

    const cancelEditor = () => {
      if (operation || unknownCreate) return;
      const trigger = editorTriggerRef.current;
      clearVariantEditor();
      scheduleFocus(() => available(trigger) ? trigger : fallbackFocus());
    };

    useImperativeHandle(ref, () => ({
      getActivationGuardSnapshot(){
        const child=inventory.getActivationGuardSnapshot();
        return {hasUnsavedWork:isVariantEditorDirty({target:activeEditor,sizeDraft:sizeDraftRef.current,fitSizesDraft:fitSizesRef.current,variants:canonicalVariants})||child.hasUnsavedWork,
          pendingMutation:Boolean(activationPendingRef.current||mutationController.current)||child.pendingMutation,
          unresolvedOutcome:Boolean(unknownCreate)||child.unresolvedOutcome};
      },
      resolveCurrentEditorFocus() {
        if (activeEditor) return available(inputRef.current) ? inputRef.current : fallbackFocus();
        return resolveInventoryEditorFocus() ?? fallbackFocus();
      },
      focusCurrentEditor() {
        if (activeEditor) scheduleFocus(() => inputRef.current ?? fallbackFocus());
        else inventory.scheduleFocus(() => resolveInventoryEditorFocus() ?? fallbackFocus());
      },
      discardAndOpen(next) {
        const trigger = pendingSwitchTriggerRef.current;
        pendingSwitchTriggerRef.current = null;
        clearVariantEditor();
        inventory.discardCurrent();
        requestAnimationFrame(() => {
          if (!mounted.current || productRef.current !== props.productId) return;
          if (next.domain === 'variant') openEditor(next.target, trigger);
          else if (inventory.open(next.target, trigger)) focusInventoryEditor(next.target);
        });
      },
      retireInventoryItem(input) {
        const section=inventorySection(input.variantId);
        const row=Array.from(section?.querySelectorAll<HTMLElement>('[data-inventory-id]')??[])
          .find(node=>node.dataset.inventoryId===input.inventoryItemId)??null;
        const edit=()=>row?.querySelector<HTMLElement>('[data-inventory-edit]')??section?.querySelector<HTMLElement>('h3')??fallbackFocus();
        return inventory.transitionLifecycle({
          variantId:input.variantId,
          inventoryItemId:input.inventoryItemId,
          action:'retire',
          focus:{
            edit,
            action:(action)=>row?.querySelector<HTMLElement>(`[data-inventory-lifecycle="${action}"]`)??edit(),
            condition:()=>row?.querySelector<HTMLElement>('[data-inventory-condition]')??edit(),
            heading:()=>section?.querySelector<HTMLElement>('h3')??fallbackFocus(),
          },
        });
      },
    }), [activeEditor, canonicalVariants, unknownCreate, fallbackFocus, focusInventoryEditor, inventory, inventorySection, openEditor, props.productId, resolveInventoryEditorFocus, scheduleFocus]);

    const mutationAllowed = () => !operation && !mutationController.current && !productMissing && !unknownCreate && Boolean(props.token);

    const beginMutation = (next: Exclude<Operation, null>) => {
      if (!mutationAllowed()) return null;
      activationPendingRef.current=true;
      const generation = ++mutationGeneration.current;
      refreshGeneration.current += 1;
      refreshController.current?.abort();
      mutationController.current?.abort();
      const controller = new AbortController();
      mutationController.current = controller;
      setOperation(next);
      setFieldError(null);
      setSubmitError(null);
      setFeedback(null);
      return {generation, signal: controller.signal, productId: props.productId};
    };

    const mutationSafe = (generation: number, productId: string) =>
      mounted.current && productRef.current === productId && mutationGeneration.current === generation;

    const finishMutation = (generation: number, productId: string) => {
      if (!mutationSafe(generation, productId)) return;
      setOperation(null);
      mutationController.current = null;
    };

    const handleError = (error: unknown, context: 'create' | 'update', variantId?: string) => {
      if (props.onAccessError?.(error)) return;
      if (!(error instanceof AdminApiError)) {
        setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte zadané údaje.');
        return;
      }
      if (error.kind === 'cancelled') return;
      if (error.code === 'PRODUCT_NOT_FOUND') {
        setProductMissing(true);
        setSubmitError(null);
        return;
      }
      if (error.code === 'VARIANT_SIZE_ALREADY_EXISTS') {
        setFieldError('Tato velikost už u produktu existuje.');
        scheduleFocus(() => inputRef.current);
        return;
      }
      if (error.code === 'VARIANT_NOT_FOUND' && variantId) {
        setMissingVariantId(variantId);
        setRefreshReason('missing-variant');
        setSubmitError('Velikost nelze uložit, protože varianta už nebyla nalezena.');
        return;
      }
      if (error.code === 'SKU_ALREADY_EXISTS') {
        setSubmitError('Variantu se nepodařilo uložit kvůli konfliktu SKU. SKU se v tomto kroku neupravuje. Načtěte aktuální varianty a zkontrolujte stav.');
        setRefreshReason('sku-conflict');
        return;
      }
      if (error.code === 'VALIDATION_ERROR') {
        setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte zadané údaje.');
        scheduleFocus(() => inputRef.current);
        return;
      }
      if (context === 'create' && error.kind === 'network') {
        const attempted = normalizeDraft();
        setUnknownCreate(attempted);
        setRefreshReason('unknown-create');
        setSubmitError(null);
        return;
      }
      if (context === 'update' && error.kind === 'network') {
        setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte připojení a zkuste to znovu.');
        return;
      }
      setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte zadané údaje.');
    };

    const normalizeDraft = () => sizeDraft.trim();

    const save = async () => {
      if (!activeEditor || !mutationAllowed()) return;
      if (activeEditor.kind === 'edit' && missingVariantId === activeEditor.variantId) return;
      const validation = validateVariantSize({
        value: sizeDraft,
        variants: canonicalVariants,
        editingVariantId: activeEditor.kind === 'edit' ? activeEditor.variantId : undefined,
      });
      if (validation.error) {
        setFieldError(variantSizeErrorCopy(validation.error));
        scheduleFocus(() => inputRef.current);
        return;
      }

      if (!fitSizes.length) {
        setFieldError('Vyberte alespoň jednu velikost pro filtrování.');
        return;
      }

      if (activeEditor.kind === 'add') {
        const started = beginMutation('create');
        if (!started) return;
        try {
          const variant = await createAdminVariant({
            token: props.token,
            productId: started.productId,
            body: {size: validation.value, fitSizes},
            signal: started.signal,
          });
          if (!mutationSafe(started.generation, started.productId)) return;
          if (variant.productId !== started.productId) {
            setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte zadané údaje.');
            return;
          }
          setCanonicalVariants(previous => reconcileCreatedVariant(previous, variant));
          setActiveEditor(null);
          setSizeDraft('');
          setFitSizesDraft(null);
          setUnknownCreate(null);
          setRefreshReason(null);
          setFeedback('Varianta byla přidána.');
          editorTriggerRef.current = null;
          focusEditButton(variant.id);
        } catch (error) {
          if (!mutationSafe(started.generation, started.productId)) return;
          handleError(error, 'create');
        } finally {
          finishMutation(started.generation, started.productId);
        }
        return;
      }

      const variantId = activeEditor.variantId;
      const started = beginMutation(`update:${variantId}`);
      if (!started) return;
      try {
        const variant = await updateAdminVariantSize({
          token: props.token,
          variantId,
          body: {size: validation.value, fitSizes},
          signal: started.signal,
        });
        if (!mutationSafe(started.generation, started.productId)) return;
        if (variant.id !== variantId || variant.productId !== started.productId) {
          setSubmitError('Variantu se nepodařilo uložit. Zkontrolujte zadané údaje.');
          return;
        }
        setCanonicalVariants(previous => reconcileUpdatedVariant(previous, variant));
        setActiveEditor(null);
        setSizeDraft('');
        setFitSizesDraft(null);
        setMissingVariantId(null);
        setRefreshReason(null);
        setFeedback('Velikost byla uložena.');
        editorTriggerRef.current = null;
        focusEditButton(variant.id);
      } catch (error) {
        if (!mutationSafe(started.generation, started.productId)) return;
        handleError(error, 'update', variantId);
      } finally {
        finishMutation(started.generation, started.productId);
      }
    };

    const refresh = async () => {
      if (inventory.getManualBlockGuard().hasRisk || operation || refreshing || refreshController.current || productMissing || !props.token) return;
      const observedManual=inventory.getManualBlockGuard().revision;
      const generation = ++refreshGeneration.current;
      const observedMutation = mutationGeneration.current;
      const controller = new AbortController();
      refreshController.current = controller;
      const productId = props.productId;
      setRefreshing(true);
      setSubmitError(null);
      try {
        const detailVariants = await getAdminProductVariants({token: props.token, productId, signal: controller.signal});
        if (!mounted.current || productRef.current !== productId || refreshGeneration.current !== generation ||
          mutationGeneration.current !== observedMutation || inventory.getManualBlockGuard().revision!==observedManual) return;
        const variants = sortAdminVariants(projectDetailVariants(detailVariants));
        setCanonicalVariants(variants);
        if (unknownCreate) {
          const result = reconcileUnknownCreate({variants, attemptedSize: unknownCreate});
          setUnknownCreate(null);
          setRefreshReason(null);
          if (result.found) {
            setFieldError('Tato velikost už u produktu existuje.');
            setFeedback('V aktuálních variantách už tato velikost existuje. Zkontrolujte stav před další akcí.');
          } else {
            setFieldError(null);
            setFeedback('Aktuální varianty byly načteny. Vytvoření můžete zkusit znovu.');
          }
        } else if (missingVariantId) {
          if (variants.some(variant => variant.id === missingVariantId)) {
            setMissingVariantId(null);
            setRefreshReason(null);
            setSubmitError(null);
            setFeedback('Aktuální varianty byly načteny.');
          } else {
            setRefreshReason('missing-variant');
            setFeedback('Varianta už není dostupná.');
          }
        } else {
          setRefreshReason(null);
          setFeedback('Aktuální varianty byly načteny.');
        }
      } catch (error) {
        if (!mounted.current || productRef.current !== productId || refreshGeneration.current !== generation ||
          mutationGeneration.current !== observedMutation || inventory.getManualBlockGuard().revision!==observedManual) return;
        if (props.onAccessError?.(error)) return;
        if (error instanceof AdminApiError && error.kind === 'cancelled') return;
        if (error instanceof AdminApiError && error.code === 'PRODUCT_NOT_FOUND') {
          setProductMissing(true);
          return;
        }
        setRefreshReason('refresh-failed');
        setSubmitError('Varianty se nepodařilo načíst. Zkuste to znovu.');
      } finally {
        if (mounted.current && productRef.current === productId && refreshGeneration.current === generation &&
          mutationGeneration.current === observedMutation) {
          setRefreshing(false);
          refreshController.current = null;
        }
      }
    };

    const renderEditor = (target: VariantEditorTarget) => {
      const isUpdate = target.kind === 'edit';
      const targetMissing = isUpdate && missingVariantId === target.variantId;
      const loading = operation === 'create' || (isUpdate && operation === `update:${target.variantId}`);
      return <Editor data-variant-editor>
        <Input
          ref={inputRef}
          label="Velikost"
          value={sizeDraft}
          maxLength={40}
          disabled={variantActionsDisabled}
          error={Boolean(fieldError)}
          aria-describedby={fieldError ? 'variant-size-error' : undefined}
          onChange={event => {
            sizeDraftRef.current=event.currentTarget.value;
            setSizeDraft(event.currentTarget.value);
            if (fitSizesDraft === null) fitSizesRef.current = event.currentTarget.value.trim() ? inferFitSizes(event.currentTarget.value) : [];
            setFieldError(null);
            setSubmitError(null);
          }}
        />
        {fieldError ? <ErrorText id="variant-size-error">{fieldError}</ErrorText> : null}
        <VariantFitSizesField value={fitSizes} size={sizeDraft} disabled={variantActionsDisabled} onChange={value => {
          fitSizesRef.current = value ?? (sizeDraftRef.current.trim() ? inferFitSizes(sizeDraftRef.current) : []);
          setFitSizesDraft(value); setFieldError(null); setSubmitError(null);
        }} />
        {targetMissing ? <Message><strong>Varianta už není dostupná</strong><br/>Velikost nelze uložit, protože varianta už nebyla nalezena.</Message> : null}
        <EditorActions>
          <Button variant="secondary" disabled={Boolean(operation) || Boolean(unknownCreate)} onClick={cancelEditor}>Zrušit</Button>
          <Button
            data-variant-submit
            loading={loading}
            disabled={Boolean(operation) || productMissing || Boolean(targetMissing) || Boolean(unknownCreate)}
            onClick={save}
          >
            {loading ? (isUpdate ? 'Ukládání…' : 'Přidávání…') : (isUpdate ? 'Uložit velikost' : 'Přidat variantu')}
          </Button>
        </EditorActions>
      </Editor>;
    };

    const variantActionsDisabled = Boolean(operation) || productMissing || Boolean(unknownCreate) || inventory.riskMeta.editorPendingOrUnresolved;

    return <Section ref={sectionRef} data-product-variants-section aria-labelledby="product-variants-title">
      <Divider/>
      <Header>
        <Heading id="product-variants-title" tabIndex={-1}>Varianty</Heading>
        <Button
          data-variant-add
          disabled={variantActionsDisabled}
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => requestEditor({kind: 'add'}, event.currentTarget)}
        >
          Přidat variantu
        </Button>
      </Header>

      {activeEditor?.kind === 'add' ? renderEditor(activeEditor) : null}

      {canonicalVariants.length === 0 ? <Empty data-variant-empty>
        <strong>Produkt zatím nemá žádné varianty</strong>
        <span>Přidejte první velikost produktu.</span>
        <Button
          disabled={variantActionsDisabled}
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => requestEditor({kind: 'add'}, event.currentTarget)}
        >
          Přidat variantu
        </Button>
      </Empty> : <List data-variant-list>
        {canonicalVariants.map(variant => <Item key={variant.id} data-variant-id={variant.id}>
          <Row>
            <Size data-variant-size-cell><span>Velikost</span><span data-variant-size>{variant.size}</span><small>Pro filtr: {effectiveFitSizes(variant).join(", ")}</small></Size>
            <VariantStatus>
              <StatusBadge tone={variant.status === 'active' ? 'success' : 'neutral'}>
                {variant.status === 'active' ? 'Aktivní' : 'Neaktivní'}
              </StatusBadge>
            </VariantStatus>
            <Sku>SKU: {variant.sku || 'Neuvedeno'}</Sku>
            <EditAction>
              <Button
                data-variant-edit
                size="compact"
                variant="secondary"
                disabled={variantActionsDisabled}
                onClick={(event: ReactMouseEvent<HTMLButtonElement>) =>
                  requestEditor({kind: 'edit', variantId: variant.id}, event.currentTarget)}
              >
                Upravit velikost
              </Button>
            </EditAction>
          </Row>
          {activeEditor?.kind === 'edit' && activeEditor.variantId === variant.id ? renderEditor(activeEditor) : null}
          <ProductInventoryItems variant={variant} blocks={{token:props.token,onAccessError:error=>props.onAccessError?.(error)??false}} controller={inventory} onRequestOpen={requestInventoryEditor}
            onRequestRetire={intent=>props.onRequestInventoryRetire?.(intent)}/>
        </Item>)}
      </List>}
      {activeEditor?.kind === 'edit' && !canonicalVariants.some(variant => variant.id === activeEditor.variantId)
        ? renderEditor(activeEditor)
        : null}

      {unknownCreate ? <Message data-variant-unknown>
        <strong>Výsledek vytvoření varianty není potvrzený</strong><br/>
        Požadavek mohl být zpracován. Než variantu vytvoříte znovu, načtěte aktuální varianty.
      </Message> : null}

      {productMissing ? <Message data-variant-product-missing>
        <strong>Produkt už není dostupný</strong><br/>
        Produkt už nebyl nalezen. Další změny variant nelze uložit.
      </Message> : null}

      {submitError ? <ErrorText role="alert">{submitError}</ErrorText> : null}
      {feedback ? <Message role="status" aria-live="polite">{feedback}</Message> : null}

      {refreshReason ? <Button variant="secondary" loading={refreshing} disabled={Boolean(operation) || productMissing || inventory.manualRefreshBlocked} onClick={refresh}>
        Načíst aktuální varianty
      </Button> : null}
    </Section>;
  },
);
